import React from 'react';
import { useTranslation } from 'react-i18next';

import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import Iconify from '../../../theme/iconify/iconify';

import File from './File';
import Dropzone from '../../doc/Dropzone';
import { mislecAPI } from '../../../util/axios';
import { convertFileToBase64 } from '../../util/file';
import { MaterialContext } from '../../../context/MaterialContext';

function Material({ course, user, updateUser, setOpen, setMessage, setSeverity, setRemoveMaterialId, setRemoveMaterialModal }) {
    const { material, setMaterial } = React.useContext(MaterialContext);
    const [files, setFiles] = React.useState([]);
    const [b64, setB64] = React.useState([]);

    const { t } = useTranslation();

    const fetchMaterial = () => {
        if (!user) return;

        mislecAPI.get(`/api/course_documents/${course._id}`)
            .then(res => {
                setMaterial(res.data.documents);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                setMaterial([]);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    React.useEffect(() => {
        if (material) return;
        fetchMaterial();
    }, [material]);

    React.useEffect(() => {
        const fetchDocumentData = async () => {
            let documents = [];
            for (const file of files) {
                try {
                    //  Convert file to base64
                    const base64 = await convertFileToBase64(file);

                    //  Add document to list
                    documents.push({
                        original: base64,
                        filename: file.name,
                        type: file.type,
                        size: file.size,
                        user: user._id,
                        course_id: course._id,
                        visible: false
                    });
                } catch (err) {
                    console.log(err);
                }
            }
            setB64(documents);
        }

        fetchDocumentData();
    }, [files]);

    const upload = () => {
        if (user && user.role === 'student') return;

        if (files.length === 0) {
            setMessage(t('documents.modals.upload.error.noFiles'));
            setSeverity('warning');
            setOpen(true);
            return;
        }

        for (const data of b64) {
            // Send data to server
            mislecAPI.post(`/api/course_document`, data)
                .then(res => {
                    //  Notify user of successful upload
                    setMessage(t(res.data.detail));
                    setSeverity('success');
                    setOpen(true);

                    //  Clear files
                    setFiles([]);

                    //  Refresh material
                    fetchMaterial();
                })
                .catch(error => {
                    //  Notify user of error
                    setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                    setSeverity('error');
                    setOpen(true);

                    //  Expired session
                    if (error.response)
                        if (error.response.data.detail === 'api.auth.verify.401')
                            updateUser(null);
                });
        }
    }

    const download = (file) => {
        if (!user) return;

        mislecAPI.get(`/api/course_document/${file._id}/download`)
            .then(res => {
                const link = document.createElement('a');
                link.href = `data:application/pdf;base64,${res.data.original}`;
                link.download = res.data.filename;
                link.click();
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const visibility = (file) => {
        if (!user) return;

        //  Data preparation
        const data = {
            ...file,
            visible: !file.visible
        };

        //  Send data to server
        mislecAPI.put(`/api/course_document/${file._id}`, data)
            .then(res => {
                //  Notify user of success
                setMessage(t(res.data.detail));
                setSeverity('success');
                setOpen(true);

                //  Refresh material
                setMaterial((prev) => {
                    const index = prev.findIndex(doc => doc._id === file._id);
                    prev[index].visible = !prev[index].visible;
                    return [...prev];
                })
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const remove = (file) => {
        setRemoveMaterialId(file._id);
        setRemoveMaterialModal(true);
    }

    return (
        <>
            <Grid container sx={{ ml: 1, pr: 2 }}>
                <Grid container spacing={4} sx={{ mt: 1, mb: 2, pt: 0 }}>
                    {user && user?.role !== 'student' &&
                        <>
                            <Grid item xs={12} sx={{ pt: '0px !important' }}>
                                <Typography variant='h5'>{t('course.tabs.material.upload.title')}</Typography>
                            </Grid>
                            <Grid item xs={12} sx={{ pt: '16px !important' }}>
                                <Dropzone fileList={files} onFilesSelected={setFiles} />
                            </Grid>
                            <Grid item xs={12} sx={{ pt: '16px !important', display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }} >
                                <Button
                                    variant=''
                                    disabled={files.length === 0}
                                    onClick={() => upload()}
                                    sx={{
                                        ml: {
                                            xs: 0,
                                            md: 1
                                        },
                                        borderRadius: '8px !important',
                                        backgroundColor: (theme) => ({
                                            xs: alpha(theme.palette.primary.main, 0.08),
                                            md: 'rgba(33, 43, 54, 0.08)'
                                        }),
                                        color: {
                                            xs: 'primary.main',
                                            md: 'inherit'
                                        },
                                        '&:hover': {
                                            color: 'primary.main',
                                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
                                        }
                                    }}
                                >
                                    {t('documents.modals.upload.buttons.upload')}
                                </Button>
                            </Grid>
                            <Grid item xs={12} sx={{ mb: 2, mt: 0 }}>
                                <Divider />
                            </Grid>
                        </>
                    }
                    <Grid item xs={12} sx={{ py: '0px !important' }}>
                        <Typography variant='h5'>{t('course.tabs.material.extra.title')}</Typography>
                    </Grid>
                    <Grid item xs={12} sx={{ pt: '16px !important' }}>
                        {material && material.length > 0 &&
                            material.map((file) =>
                                <File
                                    key={file._id}
                                    file={file}
                                    user={user}
                                    download={download}
                                    visibility={visibility}
                                    remove={remove}
                                />
                            )
                        }
                        {material && material.length === 0 &&
                            <Grid container>
                                <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
                                    <Iconify icon='majesticons:presentation-chart-line' width={36} sx={{ mr: 2 }} />
                                    <Grid item>
                                        <Typography variant='h6' sx={{ fontSize: '0.875rem !important' }}>
                                            {t('course.tabs.material.empty.title')}
                                        </Typography>
                                        <Typography variant='caption' sx={{ fontSize: '0.8rem !important' }}>
                                            {t('course.tabs.material.empty.description')}
                                        </Typography>
                                        {user && user?.role !== 'student' &&
                                            <Typography variant='caption' sx={{ fontSize: '0.8rem !important', display: 'block' }}>
                                                {t('course.tabs.material.empty.tip.professor')}
                                            </Typography>
                                        }
                                        {user && user?.role === 'student' &&
                                            <Typography variant='caption' sx={{ fontSize: '0.8rem !important', display: 'block' }}>
                                                {t('course.tabs.material.empty.tip.user')}
                                            </Typography>
                                        }
                                    </Grid>
                                </Grid>
                            </Grid>
                        }
                        {!material &&
                            <Stack
                                direction='row'
                                alignItems='center'
                                justifyContent='center'
                                sx={{
                                    mt: 2,
                                    mb: 2
                                }}
                            >
                                <CircularProgress size={36} />
                            </Stack>
                        }
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
}

export default Material;